(() => {
  if (window.__egParScroll && typeof window.__egParScroll.detach === "function") {
    window.__egParScroll.detach();
  }

  const DESKTOP_QUERY = "(min-width: 750px)";
  const ACCORDION_REVEAL_PADDING = 16;

  const state = {
    active: false,
    details: null,
    content: null,
    grid: null,
    media: null,
    mq: null,
    offset: 0,
    accordionOffset: 0,
    lastAppliedOffset: null,
    lastScrollY: null,
    maxOffset: 0,
    stickyTop: 0,
    detailsH: 0,
    contentH: 0,
    mediaH: 0,
    mediaTop: 0,
    mediaBottom: 0,
    gridTop: 0,
    gridH: 0,
    scrollStart: 0,
    scrollEnd: 0,
    syncPending: false,
    measurePending: false,
    lastMeasureTs: 0,
    measureTimers: [],
    settleMeasureTimers: [],
    anchoredMeasureTimers: [],
    ro: null,
    mo: null,
    headerRo: null,
    headerMo: null,
    mediaMo: null,
    ignoredToggleEvents: new WeakSet(),
    onScrollBound: null,
    onResizeBound: null,
    onToggleBound: null,
    onSummaryClickBound: null,
    onMediaChangeBound: null,
    onVariantEventBound: null,
    onProductOptionChangeBound: null,
    onLoadBound: null,
  };

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const getScrollY = () => window.scrollY || window.pageYOffset || 0;

  const getViewportHeight = () => {
    return window.innerHeight || document.documentElement.clientHeight || 0;
  };

  const isDesktop = () => {
    if (!state.mq) return window.matchMedia(DESKTOP_QUERY).matches;
    return state.mq.matches;
  };

  const parsePx = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const readCssPx = (styles, name) => parsePx(styles.getPropertyValue(name));

  const getVisibleBottom = (element) => {
    if (!(element instanceof HTMLElement)) return 0;

    const styles = window.getComputedStyle(element);
    if (styles.display === "none" || styles.visibility === "hidden") return 0;

    const rect = element.getBoundingClientRect();
    if (!rect.height || rect.bottom <= 0) return 0;

    const viewportH = getViewportHeight();
    if (viewportH > 0 && rect.top >= viewportH) return 0;

    return Math.max(0, rect.bottom);
  };

  const readHeaderOffset = () => {
    const candidates = [];
    const bodyStyles = window.getComputedStyle(document.body);

    candidates.push(readCssPx(bodyStyles, "--header-height"));
    candidates.push(readCssPx(bodyStyles, "--header-group-height"));
    candidates.push(readCssPx(bodyStyles, "--sticky-header-offset"));

    const header = document.querySelector("#header-component, header-component");
    const headerSection = header ? header.closest(".header-section") : document.querySelector("#header-group .header-section");
    const headerGroup = document.querySelector("#header-group");

    candidates.push(getVisibleBottom(header));
    candidates.push(getVisibleBottom(headerSection));

    if (headerGroup instanceof HTMLElement) {
      const children = Array.from(headerGroup.children);

      for (const child of children) {
        candidates.push(getVisibleBottom(child));
      }
    }

    const viewportH = getViewportHeight();
    const maxReasonableOffset = viewportH > 0 ? Math.max(0, viewportH * 0.45) : 260;
    const offset = Math.max(0, ...candidates.filter((value) => Number.isFinite(value)));

    return Math.round(clamp(offset, 0, maxReasonableOffset));
  };

  const applyStickyBounds = () => {
    if (!state.details) return;

    state.details.style.setProperty("--eg-parscroll-sticky-top", `${state.stickyTop}px`);
    state.details.style.top = `${state.stickyTop}px`;
    state.details.style.maxHeight = `calc(100svh - ${state.stickyTop}px)`;
    state.details.style.overflow = "hidden";
  };

  const normalizeOffset = (offset) => {
    return Math.round(offset * 1000) / 1000;
  };

  const clampAccordionOffset = () => {
    const baseOffset = state.maxOffset > 0 ? state.offset : 0;
    const maxAccordionOffset = Math.max(0, state.maxOffset - baseOffset);

    state.accordionOffset = clamp(state.accordionOffset, 0, maxAccordionOffset);
  };

  const applyTransform = (offset) => {
    if (!state.content) return;

    const baseOffset = state.maxOffset > 0 ? offset : 0;

    clampAccordionOffset();

    const nextOffset = normalizeOffset(clamp(baseOffset + state.accordionOffset, 0, state.maxOffset));

    if (state.lastAppliedOffset !== null && Math.abs(nextOffset - state.lastAppliedOffset) < 0.05) {
      return;
    }

    state.lastAppliedOffset = nextOffset;
    state.content.style.transform = `translate3d(0, ${-nextOffset}px, 0)`;
  };

  const addAccordionOffset = (delta) => {
    if (!Number.isFinite(delta) || Math.abs(delta) <= 0.5) return;

    const baseOffset = state.maxOffset > 0 ? state.offset : 0;
    const maxAccordionOffset = Math.max(0, state.maxOffset - baseOffset);

    state.accordionOffset = clamp(state.accordionOffset + delta, 0, maxAccordionOffset);
    state.lastScrollY = getScrollY();

    applyTransform(state.offset);
  };

  const isVisibleForLayout = (element) => {
    if (!(element instanceof HTMLElement)) return false;
    if (element.hidden) return false;
    if (element.closest("[hidden]")) return false;

    const styles = window.getComputedStyle(element);
    if (styles.display === "none" || styles.visibility === "hidden") return false;

    return true;
  };

  const getOuterHeight = (element) => {
    if (!(element instanceof HTMLElement)) return 0;
    if (!isVisibleForLayout(element)) return 0;

    const rect = element.getBoundingClientRect();
    const styles = window.getComputedStyle(element);
    const marginTop = parsePx(styles.marginTop);
    const marginBottom = parsePx(styles.marginBottom);

    return Math.max(0, rect.height + marginTop + marginBottom);
  };

  const getGridGapY = (element) => {
    if (!(element instanceof HTMLElement)) return 0;

    const styles = window.getComputedStyle(element);
    return readCssPx(styles, "row-gap") || readCssPx(styles, "gap");
  };

  const getVisibleMediaContainers = (root) => {
    if (!(root instanceof HTMLElement)) return [];

    const containers = Array.from(root.querySelectorAll(".product-media-container"));

    return containers.filter((container) => {
      if (!(container instanceof HTMLElement)) return false;
      if (container.closest(".slideshow-controls")) return false;
      if (container.closest("zoom-dialog")) return false;
      if (container.closest(".dialog-zoomed-gallery")) return false;
      if (container.closest(".dialog-thumbnails-list")) return false;

      return isVisibleForLayout(container);
    });
  };

  const measureVisibleMediaStackHeight = (root) => {
    const containers = getVisibleMediaContainers(root);
    if (!containers.length) return 0;

    let total = 0;
    let visibleCount = 0;

    for (const container of containers) {
      const height = getOuterHeight(container);
      if (height <= 0) continue;

      total += height;
      visibleCount += 1;
    }

    if (visibleCount > 1) {
      const galleryGrid = root.querySelector(".media-gallery__grid");
      const gapSource = galleryGrid instanceof HTMLElement ? galleryGrid : root;
      total += getGridGapY(gapSource) * (visibleCount - 1);
    }

    return Math.ceil(total);
  };

  const measureNaturalMediaHeight = () => {
    if (!state.media) return 0;

    const gallery = state.media.querySelector("media-gallery");
    const galleryGrid = gallery?.querySelector(".media-gallery__grid");

    if (galleryGrid instanceof HTMLElement && isVisibleForLayout(galleryGrid)) {
      const rect = galleryGrid.getBoundingClientRect();
      const gridHeight = Math.max(rect.height || 0, galleryGrid.scrollHeight || 0);

      if (gridHeight > 0) return Math.ceil(gridHeight);
    }

    if (gallery instanceof HTMLElement) {
      const visibleStackHeight = measureVisibleMediaStackHeight(gallery);
      if (visibleStackHeight > 0) return visibleStackHeight;

      const rect = gallery.getBoundingClientRect();
      if (rect.height > 0) return Math.ceil(rect.height);
    }

    const visibleStackHeight = measureVisibleMediaStackHeight(state.media);
    if (visibleStackHeight > 0) return visibleStackHeight;

    return Math.ceil(state.media.scrollHeight || state.media.offsetHeight || state.media.getBoundingClientRect().height || 0);
  };

  const updateMediaMetrics = () => {
    if (!state.media) {
      state.mediaH = 0;
      state.mediaTop = state.gridTop;
      state.mediaBottom = state.gridTop + state.gridH;
      return;
    }

    const mediaRect = state.media.getBoundingClientRect();
    const scrollY = getScrollY();
    const naturalMediaH = measureNaturalMediaHeight();

    state.mediaH = Math.max(0, Math.ceil(naturalMediaH || mediaRect.height || 0));
    state.mediaTop = mediaRect.top + scrollY;
    state.mediaBottom = state.mediaTop + state.mediaH;
  };

  const updateMediaHeightVar = () => {
    if (!state.grid) return;

    updateMediaMetrics();

    if (state.mediaH > 0) {
      state.grid.style.setProperty("--eg-product-media-height", `${state.mediaH}px`);
    } else {
      state.grid.style.removeProperty("--eg-product-media-height");
    }
  };

  const setGridMinHeight = () => {
    if (!state.grid) return;

    const requiredHeight = Math.ceil(Math.max(state.contentH, state.detailsH));

    if (requiredHeight > 0) {
      state.grid.style.setProperty("--eg-product-details-min-height", `${requiredHeight}px`);
      state.grid.style.minHeight = "max(var(--eg-product-details-min-height), var(--eg-product-media-height, 0px))";
    } else {
      state.grid.style.removeProperty("--eg-product-details-min-height");
      state.grid.style.minHeight = "";
    }
  };

  const updateScrollBounds = () => {
    const viewportH = getViewportHeight();
    const fallbackEnd = state.gridTop + Math.max(0, state.gridH - viewportH);
    const mediaEnd = state.mediaBottom > 0 && viewportH > 0 ? state.mediaBottom - viewportH : fallbackEnd;

    state.scrollStart = Math.max(0, state.gridTop - state.stickyTop);
    state.scrollEnd = Math.max(state.scrollStart, mediaEnd);
  };

  const calculateOffset = () => {
    if (!state.active || !state.grid) return 0;

    const scrollY = getScrollY();

    return clamp(scrollY - state.scrollStart, 0, state.maxOffset);
  };

  const isAtParScrollStart = (buffer = 2) => {
    if (!state.active || !state.grid) return false;

    return getScrollY() <= state.scrollStart + buffer;
  };

  const resetAccordionOffsetAtStart = () => {
    state.offset = 0;
    state.accordionOffset = 0;
    state.lastScrollY = getScrollY();

    clampAccordionOffset();
    applyTransform(state.offset);
  };

  const sync = (forcePositionSync = false) => {
    if (!state.active || !state.details || !state.content || !state.grid) return;

    const scrollY = getScrollY();

    if (scrollY <= state.scrollStart + 1) {
      state.offset = 0;
      state.accordionOffset = 0;
      state.lastScrollY = scrollY;
      clampAccordionOffset();
      applyTransform(state.offset);
      return;
    }

    if (scrollY >= state.scrollEnd - 1) {
      state.offset = state.maxOffset;
      state.lastScrollY = scrollY;
      clampAccordionOffset();
      applyTransform(state.offset);
      return;
    }

    if (state.lastScrollY === null || forcePositionSync) {
      state.offset = calculateOffset();
      state.lastScrollY = scrollY;
      clampAccordionOffset();
      applyTransform(state.offset);
      return;
    }

    const scrollDelta = scrollY - state.lastScrollY;

    state.lastScrollY = scrollY;

    if (Math.abs(scrollDelta) < 0.01) {
      clampAccordionOffset();
      applyTransform(state.offset);
      return;
    }

    state.offset = clamp(state.offset + scrollDelta, 0, state.maxOffset);
    clampAccordionOffset();
    applyTransform(state.offset);
  };

  const measure = (force = false) => {
    if (!state.active || !state.details || !state.content || !state.grid) return;

    const now = performance.now();
    if (!force && now - state.lastMeasureTs < 80) return;
    state.lastMeasureTs = now;

    state.stickyTop = readHeaderOffset();
    applyStickyBounds();

    const detailsRect = state.details.getBoundingClientRect();
    const gridRect = state.grid.getBoundingClientRect();
    const viewportH = getViewportHeight();
    const availableH = Math.max(0, viewportH - state.stickyTop);

    state.detailsH = Math.max(0, Math.min(detailsRect.height || 0, availableH || detailsRect.height || 0));
    state.contentH = Math.max(
      0,
      state.content.scrollHeight || state.content.offsetHeight || state.content.getBoundingClientRect().height || 0
    );
    state.gridTop = gridRect.top + getScrollY();
    state.gridH = Math.max(0, gridRect.height || 0);
    state.maxOffset = Math.max(0, state.contentH - state.detailsH);

    updateMediaHeightVar();
    setGridMinHeight();

    const nextGridRect = state.grid.getBoundingClientRect();

    state.gridTop = nextGridRect.top + getScrollY();
    state.gridH = Math.max(0, nextGridRect.height || 0);

    updateMediaMetrics();
    updateScrollBounds();

    state.offset = clamp(state.offset, 0, state.maxOffset);
    clampAccordionOffset();

    sync(state.lastScrollY === null);
  };

  const runSettledMeasure = () => {
    if (!state.active) return;

    measure(true);
    sync(true);
  };

  const clearMeasureTimers = () => {
    if (!state.measureTimers.length) return;

    for (const timerId of state.measureTimers) {
      window.clearTimeout(timerId);
    }

    state.measureTimers = [];
  };

  const clearSettledMeasureTimers = () => {
    if (!state.settleMeasureTimers.length) return;

    for (const timerId of state.settleMeasureTimers) {
      window.clearTimeout(timerId);
    }

    state.settleMeasureTimers = [];
  };

  const clearAnchoredMeasureTimers = () => {
    if (!state.anchoredMeasureTimers.length) return;

    for (const timerId of state.anchoredMeasureTimers) {
      window.clearTimeout(timerId);
    }

    state.anchoredMeasureTimers = [];
  };

  const scheduleSettledMeasure = () => {
    if (!state.active) return;

    clearSettledMeasureTimers();

    runSettledMeasure();

    state.settleMeasureTimers.push(
      window.setTimeout(runSettledMeasure, 80),
      window.setTimeout(runSettledMeasure, 220),
      window.setTimeout(runSettledMeasure, 420),
      window.setTimeout(runSettledMeasure, 800),
      window.setTimeout(runSettledMeasure, 1400)
    );
  };

  const scheduleAnchoredMeasure = () => {
    if (!state.active) return;

    const anchorScrollY = getScrollY();

    const runAnchored = () => {
      if (!state.active) return;

      measure(true);

      const currentScrollY = getScrollY();
      // While the page is still scrolling (e.g. the Set Type scroll up to the photos), a forced
      // re-position would put the column where it belongs for a page at rest: it jumped for a frame.
      const scrolling = performance.now() - (state.lastScrollTs || 0) < 160;

      if (Math.abs(currentScrollY - anchorScrollY) <= 2 && !scrolling) {
        sync(true);
      } else {
        sync(false);
      }
    };

    clearAnchoredMeasureTimers();

    runAnchored();

    requestAnimationFrame(() => {
      runAnchored();

      requestAnimationFrame(() => {
        runAnchored();
      });
    });

    state.anchoredMeasureTimers.push(
      window.setTimeout(runAnchored, 80),
      window.setTimeout(runAnchored, 220),
      window.setTimeout(runAnchored, 420),
      window.setTimeout(runAnchored, 800)
    );
  };

  const scheduleSync = () => {
    if (!state.active || state.syncPending) return;

    state.syncPending = true;

    requestAnimationFrame(() => {
      state.syncPending = false;
      sync(false);
    });
  };

  const scheduleMeasure = () => {
    if (!state.measurePending) {
      state.measurePending = true;

      requestAnimationFrame(() => {
        state.measurePending = false;
        measure(true);
      });
    }

    clearMeasureTimers();

    state.measureTimers.push(
      window.setTimeout(() => measure(true), 80),
      window.setTimeout(() => measure(true), 220),
      window.setTimeout(() => measure(true), 420)
    );
  };

  const scheduleMediaMeasure = () => {
    if (!state.active) return;

    scheduleMeasure();

    requestAnimationFrame(() => {
      scheduleMeasure();

      requestAnimationFrame(() => {
        scheduleMeasure();
      });
    });

    window.setTimeout(scheduleMeasure, 700);
  };

  const scheduleProductOptionMeasure = (event) => {
    if (!state.active || !state.grid) return;

    if (!event || event.type !== "change") {
      scheduleAnchoredMeasure();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!state.grid.contains(target)) return;

    scheduleAnchoredMeasure();
  };

  const fitAccordionIntoStickyArea = (target) => {
    if (!state.active || !target || !state.details || !state.content) return;
    if (!state.content.contains(target)) return;
    if (isAtParScrollStart(2)) return;

    const detailsRect = state.details.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();

    let delta = 0;

    if (targetRect.bottom > detailsRect.bottom - ACCORDION_REVEAL_PADDING) {
      delta = targetRect.bottom - detailsRect.bottom + ACCORDION_REVEAL_PADDING;
    } else if (targetRect.top < detailsRect.top + ACCORDION_REVEAL_PADDING) {
      delta = targetRect.top - detailsRect.top - ACCORDION_REVEAL_PADDING;
    }

    addAccordionOffset(delta);
  };

  const shouldCompensateAccordionToggle = (target, shouldOpen, previousContentH) => {
    if (!state.active || !state.details || !state.content || !target) return false;
    if (!state.content.contains(target)) return false;
    if (isAtParScrollStart(2)) return false;

    if (!shouldOpen) {
      return state.accordionOffset > 0.5;
    }

    const detailsRect = state.details.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const heightDelta = state.contentH - previousContentH;

    if (heightDelta <= 0.5) return false;

    return targetRect.bottom > detailsRect.bottom - ACCORDION_REVEAL_PADDING;
  };

  const adjustAccordionAfterToggle = (target, previousContentH, shouldFitTarget, immediate = false) => {
    if (!state.active || !target || !state.details || !state.content) return;
    if (!state.content.contains(target)) return;

    let lastContentH = Number.isFinite(previousContentH) ? previousContentH : state.contentH;

    const adjust = () => {
      if (!state.active || !state.details || !state.content || !state.content.contains(target)) return;

      measure(true);

      if (target.open && isAtParScrollStart(2)) {
        resetAccordionOffsetAtStart();
        lastContentH = state.contentH;
        return;
      }

      const heightDelta = state.contentH - lastContentH;

      if (Math.abs(heightDelta) > 0.5) {
        addAccordionOffset(heightDelta);
        lastContentH = state.contentH;
      }

      if (shouldFitTarget) {
        fitAccordionIntoStickyArea(target);
      }
    };

    if (immediate) {
      adjust();
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        adjust();

        state.measureTimers.push(
          window.setTimeout(adjust, 80),
          window.setTimeout(adjust, 220),
          window.setTimeout(adjust, 420)
        );
      });
    });
  };

  const reveal = (target, previousContentH, immediate = false) => {
    adjustAccordionAfterToggle(target, previousContentH, true, immediate);
  };

  const getSummaryFromEvent = (event) => {
    if (!(event.target instanceof Element)) return null;

    const summary = event.target.closest("summary");

    if (!(summary instanceof HTMLElement)) return null;
    if (!state.content || !state.content.contains(summary)) return null;

    const details = summary.parentElement;

    if (!(details instanceof HTMLElement)) return null;
    if (details.tagName !== "DETAILS") return null;
    if (!state.content.contains(details)) return null;

    const interactive = event.target.closest("a, button, input, select, textarea, label, [role='button'], [role='link']");

    if (interactive && interactive !== summary && summary.contains(interactive)) {
      return null;
    }

    return { summary, details };
  };

  const onSummaryClick = (event) => {
    if (!state.active || !state.details || !state.content || !state.grid) return;
    if (event.defaultPrevented) return;

    const result = getSummaryFromEvent(event);

    if (!result) return;

    const target = result.details;
    const shouldOpen = !target.open;

    event.preventDefault();

    measure(true);

    const previousContentH = state.contentH;

    state.ignoredToggleEvents.add(target);
    target.open = shouldOpen;
    state.lastScrollY = getScrollY();

    measure(true);

    const shouldCompensate = shouldCompensateAccordionToggle(target, shouldOpen, previousContentH);

    if (shouldCompensate) {
      adjustAccordionAfterToggle(target, previousContentH, true, true);
      return;
    }

    if (!shouldOpen && state.accordionOffset > 0.5) {
      adjustAccordionAfterToggle(target, previousContentH, false, true);
      return;
    }

    scheduleMeasure();
  };

  const onScroll = () => {
    state.lastScrollTs = performance.now();
    scheduleSync();
  };

  const onResize = () => {
    scheduleSettledMeasure();
  };

  const onToggle = (event) => {
    const target = event.target;

    if (!target || target.tagName !== "DETAILS") return;
    if (!state.content || !state.content.contains(target)) return;

    if (state.ignoredToggleEvents.has(target)) {
      state.ignoredToggleEvents.delete(target);
      return;
    }

    const previousContentH = state.contentH;

    measure(true);

    const shouldCompensate = shouldCompensateAccordionToggle(target, target.open, previousContentH);

    if (shouldCompensate) {
      adjustAccordionAfterToggle(target, previousContentH, true, false);
      return;
    }

    scheduleMeasure();
  };

  const attachObservers = () => {
    const header = document.querySelector("#header-component, header-component");
    const headerSection = header ? header.closest(".header-section") : document.querySelector("#header-group .header-section");
    const headerGroup = document.querySelector("#header-group");

    if ("ResizeObserver" in window) {
      state.ro = new ResizeObserver(() => scheduleMeasure());
      state.ro.observe(state.content);
      state.ro.observe(state.details);
      state.ro.observe(state.grid);

      if (state.media) {
        state.ro.observe(state.media);
      }

      state.headerRo = new ResizeObserver(() => scheduleMeasure());

      if (header instanceof HTMLElement) {
        state.headerRo.observe(header);
      }

      if (headerSection instanceof HTMLElement) {
        state.headerRo.observe(headerSection);
      }

      if (headerGroup instanceof HTMLElement) {
        state.headerRo.observe(headerGroup);
      }
    } else {
      state.mo = new MutationObserver(() => scheduleMeasure());
      state.mo.observe(state.content, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    }

    if ("MutationObserver" in window) {
      state.headerMo = new MutationObserver(() => scheduleMeasure());

      if (header instanceof HTMLElement) {
        state.headerMo.observe(header, {
          attributes: true,
          attributeFilter: ["data-sticky-state", "sticky", "transparent", "class", "style"],
        });
      }

      if (headerSection instanceof HTMLElement) {
        state.headerMo.observe(headerSection, {
          attributes: true,
          attributeFilter: ["class", "style"],
        });
      }

      if (state.media instanceof HTMLElement) {
        state.mediaMo = new MutationObserver(() => scheduleMediaMeasure());
        state.mediaMo.observe(state.media, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["hidden", "aria-hidden", "class", "style"],
        });
      }
    }
  };

  const detachObservers = () => {
    if (state.ro) {
      try {
        state.ro.disconnect();
      } catch (_) {}
    }

    if (state.mo) {
      try {
        state.mo.disconnect();
      } catch (_) {}
    }

    if (state.headerRo) {
      try {
        state.headerRo.disconnect();
      } catch (_) {}
    }

    if (state.headerMo) {
      try {
        state.headerMo.disconnect();
      } catch (_) {}
    }

    if (state.mediaMo) {
      try {
        state.mediaMo.disconnect();
      } catch (_) {}
    }

    state.ro = null;
    state.mo = null;
    state.headerRo = null;
    state.headerMo = null;
    state.mediaMo = null;
  };

  const enable = () => {
    if (state.active) return;
    if (!state.details || !state.content || !state.grid) return;
    if (!isDesktop()) return;

    state.active = true;

    state.details.classList.add("eg-parscroll-enabled");

    state.content.style.willChange = "transform";
    state.content.style.transition = "none";
    state.content.style.transform = "translate3d(0, 0, 0)";

    state.onScrollBound = onScroll;
    state.onResizeBound = onResize;
    state.onToggleBound = onToggle;
    state.onSummaryClickBound = onSummaryClick;
    state.onVariantEventBound = scheduleProductOptionMeasure;
    state.onProductOptionChangeBound = scheduleProductOptionMeasure;
    state.onLoadBound = scheduleSettledMeasure;

    window.addEventListener("scroll", state.onScrollBound, { passive: true });
    window.addEventListener("resize", state.onResizeBound, { passive: true });
    window.addEventListener("orientationchange", state.onResizeBound, { passive: true });
    window.addEventListener("load", state.onLoadBound, { once: true, passive: true });

    document.addEventListener("toggle", state.onToggleBound, true);
    document.addEventListener("click", state.onSummaryClickBound, true);
    document.addEventListener("variant:update", state.onVariantEventBound, false);
    document.addEventListener("variant:selected", state.onVariantEventBound, false);
    document.addEventListener("change", state.onProductOptionChangeBound, true);

    attachObservers();

    measure(true);
    scheduleSettledMeasure();
  };

  const disable = () => {
    if (!state.active) return;

    window.removeEventListener("scroll", state.onScrollBound);
    window.removeEventListener("resize", state.onResizeBound);
    window.removeEventListener("orientationchange", state.onResizeBound);

    if (state.onLoadBound) {
      window.removeEventListener("load", state.onLoadBound);
    }

    document.removeEventListener("toggle", state.onToggleBound, true);
    document.removeEventListener("click", state.onSummaryClickBound, true);
    document.removeEventListener("variant:update", state.onVariantEventBound, false);
    document.removeEventListener("variant:selected", state.onVariantEventBound, false);
    document.removeEventListener("change", state.onProductOptionChangeBound, true);

    detachObservers();
    clearMeasureTimers();
    clearSettledMeasureTimers();
    clearAnchoredMeasureTimers();

    if (state.details) {
      state.details.classList.remove("eg-parscroll-enabled");
      state.details.style.removeProperty("--eg-parscroll-sticky-top");
      state.details.style.top = "";
      state.details.style.maxHeight = "";
      state.details.style.overflow = "";
    }

    if (state.content) {
      state.content.style.willChange = "";
      state.content.style.transition = "";
      state.content.style.transform = "";
    }

    if (state.grid) {
      state.grid.style.removeProperty("--eg-product-details-min-height");
      state.grid.style.removeProperty("--eg-product-media-height");
      state.grid.style.minHeight = "";
    }

    state.active = false;
    state.offset = 0;
    state.accordionOffset = 0;
    state.lastAppliedOffset = null;
    state.lastScrollY = null;
    state.maxOffset = 0;
    state.stickyTop = 0;
    state.detailsH = 0;
    state.contentH = 0;
    state.mediaH = 0;
    state.mediaTop = 0;
    state.mediaBottom = 0;
    state.gridTop = 0;
    state.gridH = 0;
    state.scrollStart = 0;
    state.scrollEnd = 0;
    state.syncPending = false;
    state.measurePending = false;
    state.lastMeasureTs = 0;
    state.measureTimers = [];
    state.settleMeasureTimers = [];
    state.anchoredMeasureTimers = [];
    state.ignoredToggleEvents = new WeakSet();
    state.onScrollBound = null;
    state.onResizeBound = null;
    state.onToggleBound = null;
    state.onSummaryClickBound = null;
    state.onVariantEventBound = null;
    state.onProductOptionChangeBound = null;
    state.onLoadBound = null;
  };

  const handleMediaChange = () => {
    if (isDesktop()) {
      enable();
      scheduleSettledMeasure();
    } else {
      disable();
    }
  };

  const init = () => {
    const details = document.querySelector(".product-details.sticky-content--desktop");
    if (!details) return;

    const content = details.querySelector(":scope > .group-block") || details.querySelector(".group-block");
    if (!content) return;

    const grid = details.closest("[data-product-grid-content]") || details.closest(".product-information__grid");
    if (!grid) return;

    const media = grid.querySelector(".product-information__media");

    state.details = details;
    state.content = content;
    state.grid = grid;
    state.media = media;
    state.mq = window.matchMedia(DESKTOP_QUERY);
    state.onMediaChangeBound = handleMediaChange;

    if (typeof state.mq.addEventListener === "function") {
      state.mq.addEventListener("change", state.onMediaChangeBound);
    } else if (typeof state.mq.addListener === "function") {
      state.mq.addListener(state.onMediaChangeBound);
    }

    handleMediaChange();
  };

  const detach = () => {
    disable();

    if (state.mq && state.onMediaChangeBound) {
      if (typeof state.mq.removeEventListener === "function") {
        state.mq.removeEventListener("change", state.onMediaChangeBound);
      } else if (typeof state.mq.removeListener === "function") {
        state.mq.removeListener(state.onMediaChangeBound);
      }
    }

    state.details = null;
    state.content = null;
    state.grid = null;
    state.media = null;
    state.mq = null;
    state.onMediaChangeBound = null;
  };

  window.__egParScroll = {
    detach,
    measure: () => runSettledMeasure(),
    reveal,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }
})();